import React, { ReactNode, useRef, useEffect, memo } from 'react';
import { useDrag } from '@use-gesture/react';
import { animated, useSpring } from '@react-spring/web';

import './styles.scss';

interface IModal {
  isCurrent: boolean;
  renderChildren: (ref: React.RefObject<HTMLDivElement>, closeWithAnimation: () => void) => ReactNode;
  onClose: () => void;
}

const OUT_OF_PAGE_GAP = 5000;

const Modal: React.FC<IModal> = ({ isCurrent, renderChildren, onClose }) => {
  const modalBounds = useRef<DOMRect | undefined>();
  const modalRef = useRef<HTMLDivElement>(null);
  const [{ x }, api] = useSpring(() => ({ x: OUT_OF_PAGE_GAP }));

  useEffect(() => {
    api.start(() => {
      return { x: 0, config: { tension: 250, friction: 30, velocity: 0.02 } };
    });

    modalBounds.current = modalRef.current?.getBoundingClientRect();
  }, [api]);

  const bind = useDrag(({ active, initial: [ix], down, movement: [mx] }) => {
    const startPositionFromLeftBound = ix - ((modalBounds.current?.x || 0) - OUT_OF_PAGE_GAP);
    const endPositionFromLeftBound = mx - ((modalBounds.current?.x || 0) - OUT_OF_PAGE_GAP);

    api.start(() => {
      if (startPositionFromLeftBound > 30 || mx < 0) {
        return { x: 0 };
      }

      if (endPositionFromLeftBound > 200 && !down) {
        onClose();
      }

      return { x: down ? mx : 0, immediate: active };
    });
  }, {
    axis: 'x',
    threshold: 10,
    rubberband: true,
  });

  const closeWithAnimation = () => {
    api.start(() => {
      const currentModalRect = modalRef.current?.getBoundingClientRect();

      return { x: currentModalRect?.right || 200, config: { tension: 250, friction: 30, velocity: 0.02 } };
    });

    setTimeout(onClose, 500);
  };

  return (
    <animated.div
      {...bind()}
      ref={modalRef}
      style={{ x, touchAction: 'pan-y' }}
      className={`modal-container overflow-y-scroll no-scroll ${isCurrent ? 'current' : ''}`}
    >
      {renderChildren(modalRef, closeWithAnimation)}
    </animated.div>
  );
};

export default memo(Modal);
